import { BEIGE_400, BEIGE_700, CORAL_600, GRAY_600, GRAY_700, GRAY_900, RhetorikLightFontFamily } from 'constants/styling/theme'
import { IconButton, Link, Stack, SxProps, Theme, Typography } from '@mui/material'

import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'

import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import CloseIcon from '@mui/icons-material/Close'
import { FC } from 'react'
import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import { Path } from 'constants/router'
import { PropstackIconTile } from 'components/common/PropstackLinkedBadge'
import { Link as RouterLink } from 'react-router-dom'
import { useSetPropstackBannerDismissal } from 'dataQueries'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'
import { useWorkspaceRoles } from 'utils/hooks'

interface Props {
  /** Contextual style overrides (spacing towards surrounding layout) applied to the root card */
  sx?: SxProps<Theme>
}

/**
 * Whether the Propstack purpose banner would currently render: feature flag on, active
 * workspace Propstack-linked, and not yet dismissed by the current user (ISSUE-2136).
 * Lets layout containers gate themselves on the same condition as the banner.
 */
export const usePropstackPurposeBannerVisible = (): boolean => {
  const { isPropstackLinked } = useWorkspaceRoles()
  const { currentUserWorkspaceMembership } = useUserData()
  const propstackIntegration = useFeatureFlag(FeatureFlag.PROPSTACK_INTEGRATION)

  return propstackIntegration && isPropstackLinked && !currentUserWorkspaceMembership?.propstackBannerDismissed
}

/**
 * Explains why a Propstack-provisioned workspace exists and lets the current user dismiss it.
 * Self-gating: renders nothing unless usePropstackPurposeBannerVisible() holds (ISSUE-2136).
 * @example <PropstackPurposeBanner sx={{ marginBottom: '2.4rem' }} />
 */
export const PropstackPurposeBanner: FC<Props> = ({ sx }) => {
  const { t } = useTranslation(['workspace_profile'])
  const { isWorkspaceAdmin } = useWorkspaceRoles()
  const { currentUserWorkspaceMembership } = useUserData()
  const isVisible = usePropstackPurposeBannerVisible()
  const dismissMutation = useSetPropstackBannerDismissal()
  const { spawnErrorToast } = useSnackbar()

  if (!isVisible) return null

  const handleDismiss = () => {
    const workspaceId = currentUserWorkspaceMembership?.workspaceId
    if (!workspaceId) return
    dismissMutation.mutate(
      { workspaceId, dismissed: true },
      {
        onSuccess: () => logAnalyticsEvent(AnalyticsEvent.PROPSTACK_BANNER_DISMISSED, { workspaceId }),
        onError: () => spawnErrorToast(t('propstack_banner.update_error')),
      }
    )
  }

  return (
    <Stack
      data-testid="propstack-purpose-banner"
      direction="row"
      alignItems="flex-start"
      gap="1.7rem"
      sx={[
        { background: BEIGE_400, border: `1px solid ${BEIGE_700}`, borderRadius: '1.2rem', padding: '1.9rem 2rem', width: '100%', boxSizing: 'border-box' },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >

      <PropstackIconTile size={44} />

      <Stack flex={1} minWidth={0}>

        <Typography fontFamily={RhetorikLightFontFamily} fontWeight={300} fontSize="1.7rem" letterSpacing="-.035em" lineHeight={1.3} color={GRAY_900}>
          {t('propstack_banner.title')}
        </Typography>

        <Typography variant="text-sm" color={GRAY_700} lineHeight={1.55} marginTop=".7rem" maxWidth="82rem">
          {t('propstack_banner.body')}
        </Typography>

        {isWorkspaceAdmin &&
          <Link
            component={RouterLink}
            to={Path.WORKSPACE_SETTINGS_INTEGRATIONS}
            variant="text-sm"
            onClick={() => logAnalyticsEvent(AnalyticsEvent.PROPSTACK_BANNER_LEARN_MORE_CLICKED, { workspaceId: currentUserWorkspaceMembership?.workspaceId, source: 'banner' })}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: '.5rem', marginTop: '1.1rem', width: 'fit-content', color: CORAL_600, fontWeight: 500, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            {t('propstack_banner.learn_more')}
            <ArrowForwardRoundedIcon sx={{ fontSize: '1.4rem' }} />
          </Link>
        }

      </Stack>

      <IconButton aria-label={t('propstack_banner.dismiss')} size="small" disabled={dismissMutation.isPending} onClick={handleDismiss} sx={{ color: GRAY_600 }}>
        <CloseIcon fontSize="small" />
      </IconButton>

    </Stack>
  )
}
